import type { FC, ReactNode } from 'react'
import type { Theme } from '@/components/generals/theme-provider'

import Link from 'next/link'
import {
    Building2,
    Keyboard,
    Search,
    UserRound,
    UsersRound
} from 'lucide-react'
import { getInitials } from '@/lib/format'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { useTheme } from '@/components/generals/theme-provider'
import { useDashboard } from '@/components/dashboard/dashboard-provider'
import { CURRENT_USER } from '@/data/team'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuLinkItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

const THEME_OPTIONS: { value: Theme, label: string }[] = [
    { value: 'light', label: 'Terang' },
    { value: 'dark', label: 'Gelap' },
    { value: 'system', label: 'Ikuti sistem' }
]

export const UserMenu: FC = (): ReactNode => {
    const { theme, setTheme } = useTheme()
    const { setShortcutsOpen, setCommandOpen, isMac } = useDashboard()

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                className='flex items-center gap-2.5 rounded-full p-0.5 transition-colors outline-none hover:bg-muted focus-visible:ring-3 focus-visible:ring-ring/50 sm:rounded-lg sm:py-1 sm:pr-2.5 sm:pl-1'
                aria-label='Menu akun'
            >
                <Avatar className='size-8'>
                    <AvatarFallback className='bg-foreground text-xs font-semibold text-background'>
                        {getInitials(CURRENT_USER.name)}
                    </AvatarFallback>
                </Avatar>

                <span className='hidden text-left leading-tight sm:block'>
                    <span className='block text-sm font-medium text-foreground'>
                        {CURRENT_USER.name}
                    </span>
                    <span className='block text-xs text-muted-foreground'>
                        Pemilik
                    </span>
                </span>
            </DropdownMenuTrigger>

            <DropdownMenuContent align='end' sideOffset={8} className='w-64'>
                <DropdownMenuGroup>
                    <DropdownMenuLabel className='px-2 py-1.5'>
                        <span className='block text-sm font-medium text-foreground'>
                            {CURRENT_USER.name}
                        </span>
                        <span className='block truncate text-xs font-normal text-muted-foreground'>
                            {CURRENT_USER.email}
                        </span>
                    </DropdownMenuLabel>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                    <DropdownMenuLinkItem render={<Link href='/pengaturan' />}>
                        <UserRound />
                        Profil saya
                    </DropdownMenuLinkItem>

                    <DropdownMenuLinkItem render={<Link href='/pengaturan?tab=workspace' />}>
                        <Building2 />
                        Pengaturan workspace
                    </DropdownMenuLinkItem>

                    <DropdownMenuLinkItem render={<Link href='/tim' />}>
                        <UsersRound />
                        Anggota tim
                    </DropdownMenuLinkItem>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                    <DropdownMenuLabel>
                        Tema tampilan
                    </DropdownMenuLabel>

                    <DropdownMenuRadioGroup value={theme} onValueChange={(value) => setTheme(value as Theme)}>
                        {THEME_OPTIONS.map((option) => (
                            <DropdownMenuRadioItem key={option.value} value={option.value}>
                                {option.label}
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                </DropdownMenuGroup>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                    <DropdownMenuItem onClick={() => setShortcutsOpen(true)}>
                        <Keyboard />
                        Pintasan keyboard
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => setCommandOpen(true)}>
                        <Search />
                        Cari cepat
                        <DropdownMenuShortcut>
                            {isMac ? '⌘' : 'Ctrl'} K
                        </DropdownMenuShortcut>
                    </DropdownMenuItem>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
